/**
 * ProgressIndicatorWrapper Component
 *
 * Displays an animated progress indicator for IP registration status tracking.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.showProgressIndicator - Controls visibility of the progress indicator
 * @param {string} props.ipStatus - Current status of the IP (checking, registering, pending, inactive, active, failed, apiFailed, timeout)
 * @param {number} props.getProgressValueAndStatusApiCallCount - API call count for progress calculation
 * @returns {JSX.Element} Progress indicator wrapped in a styled container
 *
 * @example
 * <ProgressIndicatorWrapper
 *   showProgressIndicator={true}
 *   ipStatus="pending"
 *   getProgressValueAndStatusApiCallCount={0}
 * />
 *
 * Features:
 * - Auto-rotating status messages during pending state (every 2 seconds)
 * - 10-minute timeout for long-running operations
 * - Smooth transitions when showing/hiding the indicator
 * - Status-specific messaging for all registration states
 */

import { useEffect, useRef, useState } from 'react'
import ProgressIndicator from './common/ProgressIndicator'
import useTimedProgress from './common/hooks/useTimedProgress'
import styles from '@/scss/Home.module.scss'

const labelMap = {
  checking: 'Checking registration...',
  registering: 'Registering IP...',
  pending: [
    'Almost halfway there...',
    'Making progress...',
    'Picking up speed...',
    'Things are moving...',
    'Gaining momentum...',
    'Right on track...',
    'Getting closer...',
    'Just a bit more...',
    'Wrapping things up...',
    'On it...',
  ],
  inactive: 'Your IP is inactive.',
  active: 'Your IP is active!',
  failed: 'Registration failed :(',
  apiFailed: 'Unable to reach server :(',
  timeout: 'Request timed out :(',
}

export default function ProgressIndicatorWrapper({
  showProgressIndicator,
  ipStatus,
  getProgressValueAndStatusApiCallCount,
}) {
  const [showProgress, toggleProgressIndicator] = useState(false)
  const [label, setLabel] = useState(
    typeof labelMap[ipStatus] === 'string' ? labelMap[ipStatus] : labelMap[ipStatus]?.[0]
  )

  const startedAt = useRef(null)

  const progressValue = useTimedProgress(
    startedAt.current,
    10 * 60 * 1000,
    getProgressValueAndStatusApiCallCount,
    showProgress
  )

  useEffect(() => {
    if (showProgress !== showProgressIndicator) {
      if (showProgressIndicator) {
        startedAt.current = Date.now()
        toggleProgressIndicator(true)
      } else {
        setTimeout(() => {
          toggleProgressIndicator(showProgressIndicator)
        }, 500)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProgressIndicator])

  useEffect(() => {
    if (ipStatus === 'pending') {
      const labels = labelMap[ipStatus]
      let index = 0
      setLabel(labels[index])
      index = (index + 1) % labels.length
      const intervalId = setInterval(() => {
        setLabel(labels[index])
        index = (index + 1) % labels.length
      }, 2000)

      return () => clearInterval(intervalId)
    } else {
      setLabel(labelMap[ipStatus] || '')
    }
  }, [ipStatus])

  return (
    <div
      className={`${styles.progressIndicatorContainer} ${showProgress ? styles.openProgressIndicator : ''}`}
    >
      <ProgressIndicator value={progressValue} size={320} label={label} />
    </div>
  )
}
